/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ConfusionMatrixChart } from 'src/core/components/ConfusionMatrixChart';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { ChartAxisData, ConfusionMatrixYAxis } from 'src/core/types/chart';
import { ConfusionAggregate } from 'src/luis/projects/testPane/utils/batchTestResultsCalculator';
import { LuisId } from 'src/luis/types/Resource';
import { ConfusionType } from 'src/luis/types/stores/testPane/ConfusionType';

export type BatchTestResultGraphProps = {
    /**
     * The confusion results to show.
     */
    confusions: ConfusionAggregate;

    /**
     * A callback for when an utterance point
     * is clicked.
     */
    onUtterancePointClick: (id: LuisId) => void;

    /**
     * A callback for when a confusion type in the
     * chart is clicked.
     */
    onConfusionClick: (confusionType: ConfusionType) => void;
};

const messages = defineMessages({
    truePositive: {
        id: 'testPane.batchTestResultGraph.truePositive',
        defaultMessage: 'True Positive'
    },
    falsePositive: {
        id: 'testPane.batchTestResultGraph.falsePositive',
        defaultMessage: 'False Positive'
    },
    trueNegative: {
        id: 'testPane.batchTestResultGraph.trueNegative',
        defaultMessage: 'True Negative'
    },
    falseNegative: {
        id: 'testPane.batchTestResultGraph.falseNegative',
        defaultMessage: 'False Negative'
    }
});

const { styleComponent } = getStylist('BatchTestResultGraph');
const batchResultsTheme = getScopedTheme('BatchTestResultGraph');

const ConfusionGraphContainer = styleComponent<IStackProps>(Stack)('ConfusionGraphContainer', { position: 'relative' });

const ConfusionLink = styleComponent<ILinkProps>(Link)<{
    positive: boolean;
    position: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}>(
    'ConfusionLink',
    theme => ({ zIndex: theme.linkZIndex as Property.ZIndex, position: 'absolute' }),
    ({ customProps }, theme) => {
        let positionStyle;
        const colorStyle = { color: customProps.positive ? theme.positiveColor : theme.negativeColor };

        switch (customProps.position) {
            case 'topRight':
                positionStyle = { top: 10, right: 0 };
                break;
            case 'bottomLeft':
                positionStyle = { bottom: 0, left: 50 };
                break;
            case 'bottomRight':
                positionStyle = { bottom: 0, right: 0 };
                break;
            case 'topLeft':
            default:
                positionStyle = { top: 10, left: 50 };
                break;
        }

        return { ...colorStyle, ...positionStyle };
    }
);

/**
 * Renders a confusion matrix with the data results
 * of the batch test.
 */
export const BatchTestResultGraph = (props: BatchTestResultGraphProps) => {
    const { confusions, onUtterancePointClick, onConfusionClick } = props;
    const axisData = React.useRef<ReturnType<typeof getAxisData>>();
    const { localize } = useLocalization();

    axisData.current = getAxisData(confusions);

    return (
        <ConfusionGraphContainer verticalFill>
            <ConfusionLink
                title="Test title"
                customProps={{ position: 'topLeft', positive: true }}
                onClick={() => onConfusionClick(ConfusionType.TruePositive)}>
                <LuisText variant="large" fontWeight={batchResultsTheme.confusionLinkFontWeight}>
                    {localize(messages.truePositive)}
                </LuisText>
            </ConfusionLink>

            <ConfusionLink
                customProps={{ position: 'topRight', positive: false }}
                onClick={() => onConfusionClick(ConfusionType.FalsePositive)}>
                <LuisText variant="large" fontWeight={batchResultsTheme.confusionLinkFontWeight}>
                    {localize(messages.falsePositive)}
                </LuisText>
            </ConfusionLink>

            <ConfusionLink
                customProps={{ position: 'bottomLeft', positive: false }}
                onClick={() => onConfusionClick(ConfusionType.FalseNegative)}>
                <LuisText variant="large" fontWeight={batchResultsTheme.confusionLinkFontWeight}>
                    {localize(messages.falseNegative)}
                </LuisText>
            </ConfusionLink>

            <ConfusionLink
                customProps={{ position: 'bottomRight', positive: true }}
                onClick={() => onConfusionClick(ConfusionType.TrueNegative)}>
                <LuisText variant="large" fontWeight={batchResultsTheme.confusionLinkFontWeight}>
                    {localize(messages.trueNegative)}
                </LuisText>
            </ConfusionLink>

            <ConfusionMatrixChart
                xData={axisData.current.xData}
                yData={axisData.current.yData}
                onPointClick={data => onUtterancePointClick(getClickedUtterance(axisData.current.yData, data))}
            />
        </ConfusionGraphContainer>
    );
};

/**
 * Gets the clicked utterance id from the data passed
 * from the confusion matrix.
 *
 * @param hayStack The data to search.
 * @param needle The data to search for.
 * @returns The utterance id if found.
 */
const getClickedUtterance = (hayStack: ChartAxisData<ConfusionMatrixYAxis>, needle: ConfusionMatrixYAxis) => {
    const dataPoint = hayStack.data.find(d => d.xValue === needle.xValue && d.score === needle.score);

    return dataPoint && dataPoint.id;
};

/**
 * Generates axis data based on the results.
 *
 * @param model The luis model to plot the results for.
 */
const getAxisData = (confusionData: ConfusionAggregate) => {
    const step: number = 1;
    const stepIndex = new Map<ConfusionType, number>();
    const xData: ChartAxisData<number> = { label: 'xData', data: [] };
    const yData: ChartAxisData<ConfusionMatrixYAxis> = { label: 'yData', data: [] };

    if (!confusionData) {
        return { xData, yData };
    }

    stepIndex.set(ConfusionType.TruePositive, 1);
    stepIndex.set(ConfusionType.TrueNegative, 1);
    stepIndex.set(ConfusionType.FalsePositive, 1);
    stepIndex.set(ConfusionType.FalseNegative, 1);

    Array.from(confusionData.scores.keys()).forEach(id => {
        confusionData.scores.get(id).forEach(data => {
            let yScore: number = data.score;

            switch (data.confusion) {
                case ConfusionType.TruePositive:
                    xData.data.push(step * stepIndex.get(ConfusionType.TruePositive) * -1);
                    stepIndex.set(ConfusionType.TruePositive, stepIndex.get(ConfusionType.TruePositive) + 1);
                    break;
                case ConfusionType.TrueNegative:
                    yScore -= 1;
                    xData.data.push(step * stepIndex.get(ConfusionType.TrueNegative) * 1);
                    stepIndex.set(ConfusionType.TrueNegative, stepIndex.get(ConfusionType.TrueNegative) + 1);
                    break;
                case ConfusionType.FalsePositive:
                    xData.data.push(step * stepIndex.get(ConfusionType.FalsePositive) * 1);
                    stepIndex.set(ConfusionType.FalsePositive, stepIndex.get(ConfusionType.FalsePositive) + 1);
                    break;
                case ConfusionType.FalseNegative:
                    yScore -= 1;
                    xData.data.push(step * stepIndex.get(ConfusionType.FalseNegative) * -1);
                    stepIndex.set(ConfusionType.FalseNegative, stepIndex.get(ConfusionType.FalseNegative) + 1);
                    break;
                default:
            }

            yData.data.push({ id, score: yScore, xValue: xData.data[xData.data.length - 1] });
        });
    });

    return { xData, yData };
};
